import { Merchant } from 'Utils/types/JOONAdmin/merchant'
import { getRequest } from 'Services/baseRequests/getRequest'
import { RequestResponse } from 'Services/apiResponseTypes'
import { postRequest } from 'Services/baseRequests/postRequest'
import { putRequest } from 'Services/baseRequests/putRequest'
import { BenefitProgramMerchants, EmployerBenefitProgram } from 'Utils/types/benefitProgram'
import { MerchantFormValues } from 'Pages/JOONAdmin/EmployerDetailsPage/Views/BenefitPrograms/CreateBenefitProgramModal/MerchantForm'
import {
  GetDetailedMerchantResponse,
  GetMerchantDetailsApiResponse,
  GetMerchantsResponse
} from 'Services/JOONAdmin/adminApiResponseTypes'
import { parseJoonAdminMerchantApiResponseData } from 'Services/merchants/parsers'
import { BasicCategoryAssociation } from 'Utils/types/basicBenefitProgramCategory'
import { BenefitProgramCategory } from 'Utils/types/benefitProgramCategory'

export type GetMerchantsPayload = {
  perPage?: number
  pageNumber?: number
  searchText?: string
  sortOrder?: 'asc' | 'desc'
}

export type AssociationPayload = {
  basic_benefit_program_category_ids: number[]
  merchant_ids: number[]
}

export type DisassociationPayload = {
  basic_benefit_program_category_id: number
}

export const getMerchants = async ({
  searchText,
  perPage,
  pageNumber,
  sortOrder
}: GetMerchantsPayload): Promise<GetMerchantsResponse> => {
  const response = await getRequest<GetMerchantsResponse>(
    `/merchants?search_text=${encodeURIComponent(searchText ?? '')}&perpage=${perPage || 10}&page=${
      pageNumber || 1
    }&sort=${sortOrder ?? 'asc'}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data,
    total_records: response?.total_records ?? 0,
    total_pages: response?.total_pages ?? 0
  }
}

export type GetBenefitProgramMerchantsResponse = RequestResponse<BenefitProgramMerchants>

export const getBenefitProgramMerchants = async (
  benefitProgramId: EmployerBenefitProgram['id']
): Promise<GetBenefitProgramMerchantsResponse> => {
  const response = await getRequest<GetBenefitProgramMerchantsResponse>(
    `/admin/benefit_programs/${benefitProgramId}/fetch_program_merchants`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export type AddMerchantToProgramPayload = {
  programId: EmployerBenefitProgram['id']
  action: string
  categoryId?: BenefitProgramCategory['id']
} & MerchantFormValues

type AddMerchantToProgramApiPayload = {
  list_type: string
  merchant_id?: Merchant['id']
  merchant_name?: Merchant['name']
}

export const addMerchantToProgram = async ({
  programId,
  selectedMerchant,
  merchantName,
  categoryId,
  action
}: AddMerchantToProgramPayload): Promise<RequestResponse> => {
  const payload = {
    merchant_id: selectedMerchant,
    merchant_name: merchantName,
    list_type: action,
    basic_category_id: categoryId
  }
  const response = await putRequest<RequestResponse, AddMerchantToProgramApiPayload>(
    `/admin/benefit_programs/${programId}/benefit_program_merchants`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export type RemoveMerchantFromListPayload = {
  programId: EmployerBenefitProgram['id']
  merchantId: Merchant['id']
}

export const removeMerchantFromAllowList = async ({
  programId,
  merchantId
}: RemoveMerchantFromListPayload): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse>(
    `/admin/benefit_programs/${programId}/benefit_program_merchants/${merchantId}/remove_from_allow_list`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export const removeMerchantFromBlockList = async ({
  programId,
  merchantId
}: RemoveMerchantFromListPayload): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse>(
    `/admin/benefit_programs/${programId}/benefit_program_merchants/${merchantId}/remove_from_block_list`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export type AddMerchantPayload = {
  name: string
}

export const addMerchant = async ({
  name
}: AddMerchantPayload): Promise<GetDetailedMerchantResponse> => {
  const response = await postRequest<GetMerchantDetailsApiResponse, { name: string }>(
    `/merchants`,
    {
      name
    }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
      ? { merchant: parseJoonAdminMerchantApiResponseData(response.data) }
      : undefined
  }
}

export type GetMerchantCategoriesResponse = RequestResponse<GetMerchantCategoriesResponseData>
export type GetMerchantCategoriesResponseData = {
  basic_benefit_program_categories: BasicCategoryAssociation[]
}

export const getMerchantCategories = async (
  merchantId: number
): Promise<GetMerchantCategoriesResponse> => {
  const response = await getRequest<GetMerchantCategoriesResponse>(`/merchants/${merchantId}`)

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data
  }
}

export type AssociateMerchantsCategoriesPayload = {
  merchantIds: number[]
  categoryIds: number[]
}

export type DisassociateMerchantCategoryPayload = {
  merchantId: number
  categoryId: number
}

export const associateMerchantsCategories = async ({
  merchantIds,
  categoryIds
}: AssociateMerchantsCategoriesPayload): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, AssociationPayload>(
    `/merchants/bulk_associate`,
    {
      basic_benefit_program_category_ids: categoryIds,
      merchant_ids: merchantIds
    }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export const disassociateMerchantCategory = async ({
  merchantId,
  categoryId
}: DisassociateMerchantCategoryPayload): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, DisassociationPayload>(
    `/merchants/${merchantId}/disassociate`,
    {
      basic_benefit_program_category_id: categoryId
    }
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export type UncategorizedMerchant = {
  transaction_id: number
  merchant_name: string
  most_recent_approval_date: string
  employer_name: string
  employer_id: number
  benefit_program_name: string
  benefit_program_id: number
  user_id: number
  reported_category: string | null
  reported_category_id: number | null
  reported_note: string | null
}

export type GetUncategorizedMerchantsApiResponse = {
  success: boolean
  message: string
  data: UncategorizedMerchant[]
  total_count: number
  total_pages: number
  current_page: number
}

export type GetUncategorizedMerchantsPayload = {
  page?: number
  perPage?: number
  employerId?: number
  sortBy?: string
  sortDirection?: 'asc' | 'desc'
  search?: string
}

export const getUncategorizedMerchants = async ({
  page = 1,
  perPage = 20,
  employerId,
  sortBy,
  sortDirection,
  search
}: GetUncategorizedMerchantsPayload = {}): Promise<GetUncategorizedMerchantsApiResponse> => {
  const employerParam = employerId ? `&employer_id=${employerId}` : ''
  const sortParams = sortBy ? `&sort_by=${sortBy}&sort_direction=${sortDirection ?? 'asc'}` : ''
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : ''
  const response = await getRequest<GetUncategorizedMerchantsApiResponse>(
    `/admin/merchant_review/uncategorized_merchants?page=${page}&per_page=${perPage}${employerParam}${sortParams}${searchParam}`
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    data: response?.data ?? [],
    total_count: response?.total_count ?? 0,
    total_pages: response?.total_pages ?? 0,
    current_page: response?.current_page ?? 1
  }
}

export type CategorizeMerchantPayload = {
  merchant_name: string
  category_type: 'global' | 'program_specific'
  basic_category_id: number
  benefit_program_id?: number
}

export const categorizeMerchant = async (
  payload: CategorizeMerchantPayload
): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, CategorizeMerchantPayload>(
    `/admin/merchant_review/categorize`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}

export type DeclineMerchantPayload = {
  transaction_id: number
}

export type DeclineMerchantResponse = RequestResponse & {
  errors?: string[]
}

export const declineMerchant = async (
  payload: DeclineMerchantPayload
): Promise<DeclineMerchantResponse> => {
  const response = await postRequest<DeclineMerchantResponse, DeclineMerchantPayload>(
    `/admin/merchant_review/decline`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    errors: response?.errors as string[] | undefined
  }
}

export type BulkDeclinePayload = {
  transaction_ids: number[]
}

export type BulkDeclineSkippedDetail = {
  transaction_id: number
  name: string
  reason: string
}

export type BulkDeclineResponse = RequestResponse & {
  declined_count?: number
  skipped_count?: number
  skipped_details?: BulkDeclineSkippedDetail[]
  errors?: string[]
}

export const bulkDeclineMerchants = async (
  payload: BulkDeclinePayload
): Promise<BulkDeclineResponse> => {
  const response = await postRequest<BulkDeclineResponse, BulkDeclinePayload>(
    `/admin/merchant_review/bulk_decline`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? '',
    declined_count: response?.declined_count,
    skipped_count: response?.skipped_count,
    skipped_details: response?.skipped_details,
    errors: response?.errors as string[] | undefined
  }
}

export type BulkCategorizeItem = {
  merchant_name: string
  basic_category_id: number
}

export type BulkCategorizePayload = {
  merchants: BulkCategorizeItem[]
}

export const bulkCategorizeMerchants = async (
  payload: BulkCategorizePayload
): Promise<RequestResponse> => {
  const response = await postRequest<RequestResponse, BulkCategorizePayload>(
    `/admin/merchant_review/bulk_categorize`,
    payload
  )

  return {
    success: response?.success ?? false,
    message: response?.message ?? ''
  }
}
